import React from 'react';
import { 
  Rocket, 
  Palette, 
  Settings, 
  Database, 
  Monitor, 
  CheckCircle,
  MessageCircle,
  Search,
  Heart,
  Users,
  Globe,
  Clock
} from 'lucide-react';
import { Card, CardContent } from '../components/ui';

const phases = [
  {
    id: 1,
    title: "Phase 1",
    description: "We Create Your AI",
    details: [
      "You get a generic AI (a blank canvas)",
      "Test Environment on Web"
    ],
    icon: Settings,
    color: "from-pink-100 to-pink-200",
    borderColor: "border-pink-200",
    textColor: "text-pink-800"
  },
  {
    id: 2,
    title: "Phase 2",
    description: "We customize According to look & feel",
    details: [
      "Customize TOV to match brand"
    ],
    icon: Palette,
    color: "from-orange-100 to-orange-200",
    borderColor: "border-orange-200",
    textColor: "text-orange-800"
  },
  {
    id: 3,
    title: "Phase 3",
    description: "Finetune the Tone of voice based on feedback",
    details: [
      "Tweak UI colors, icons, URL behavior etc"
    ],
    icon: Settings,
    color: "from-yellow-100 to-yellow-200",
    borderColor: "border-yellow-200",
    textColor: "text-yellow-800"
  },
  {
    id: 4,
    title: "Phase 4",
    description: "We connect your hotel-specific dataset",
    details: [
      "Inhouse docs connected and updates within 24hs. Also applies to web scraping."
    ],
    icon: Database,
    color: "from-blue-100 to-blue-200",
    borderColor: "border-blue-200",
    textColor: "text-blue-800"
  },
  {
    id: 5,
    title: "Phase 5",
    description: "More UI adjustments if needed",
    details: [
      "Beta Launch"
    ],
    icon: Monitor,
    color: "from-cyan-100 to-cyan-200",
    borderColor: "border-cyan-200",
    textColor: "text-cyan-800"
  },
  {
    id: 6,
    title: "Phase 6",
    description: "GA Launch 🚀",
    details: [
      "General Availability"
    ],
    icon: Rocket,
    color: "from-green-100 to-green-200",
    borderColor: "border-green-200",
    textColor: "text-green-800"
  }
];

export function Info() {
  return (
    <div className="min-h-screen bg-cover bg-center bg-no-repeat" style={{ backgroundImage: 'url(/Gradient.avif)' }}>
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="text-center mb-12 pt-16">
          <h1 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-4">
          Your Personal Hotel Assistant
          </h1>
          <div className="w-24 h-1 bg-gradient-to-r from-green-400 to-green-600 mx-auto mb-6"></div>
          <p className="text-lg text-gray-600 font-text max-w-3xl mx-auto leading-relaxed">
            Guest Scout offers 24/7, 365 guest service on our external websites. While it uses the same underlying technology as our internal Scout, it features a different user interface designed specifically for our guests.
          </p>
        </div>

        <div>
          {/* Chat Launcher Info */}
          <div className="bg-white/60 backdrop-blur-xl rounded-2xl border border-gray-200 p-8 mb-12">
            <div className="flex items-start space-x-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-8 h-8 text-blue-700" />
              </div>
              <div>
                <h3 className="text-2xl font-display font-bold text-gray-900 mb-4">
                  The Chat Launcher
                </h3>
                <p className="text-gray-600 font-text leading-relaxed">
                  Look for a small, circular icon, usually in the corner of the hotel's webpage. This is the launcher. Clicking on it will open the chat window and start your conversation with Scout.
                </p>
              </div>
            </div>
          </div>

          {/* What Does the Chatbot Do */}
          <div className="mb-12">
            <h3 className="text-2xl font-display font-bold text-gray-900 mb-8 text-center">
              What Does the Chatbot Do for Guests?
            </h3>
            <p className="text-lg text-gray-600 font-text text-center mb-8 max-w-3xl mx-auto">
              Scout's main goal is to help you get the most out of your visit. It's there to:
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="bg-white/70 backdrop-blur-xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Search className="w-8 h-8 text-green-700" />
                  </div>
                  <h4 className="text-lg font-display font-bold text-gray-900 mb-3">
                    Provide Instant Answers
                  </h4>
                  <p className="text-sm text-gray-600 font-text">
                    Ask questions about the hotel, like its address, amenities, or restaurant hours, and get an immediate response.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white/70 backdrop-blur-xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-purple-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Heart className="w-8 h-8 text-purple-700" />
                  </div>
                  <h4 className="text-lg font-display font-bold text-gray-900 mb-3">
                    Offer Personalized Recommendations
                  </h4>
                  <p className="text-sm text-gray-600 font-text">
                    Based on your questions, Scout can suggest local attractions, dining options, or activities to enhance your stay.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white/70 backdrop-blur-xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-orange-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-8 h-8 text-orange-700" />
                  </div>
                  <h4 className="text-lg font-display font-bold text-gray-900 mb-3">
                    Available 24/7
                  </h4>
                  <p className="text-sm text-gray-600 font-text">
                    Get instant answers to your questions anytime, day or night. No waiting for business hours or phone queues.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Conversation Window */}
          <div className="bg-white/60 backdrop-blur-xl rounded-2xl border border-gray-200 p-8 mb-12">
            <div className="flex items-start space-x-6">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-100 to-pink-200 rounded-2xl flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-8 h-8 text-pink-700" />
              </div>
              <div>
                <h3 className="text-2xl font-display font-bold text-gray-900 mb-4">
                  The Conversation Window
                </h3>
                <p className="text-gray-600 font-text leading-relaxed">
                  Once you click the launcher, a clean and simple chat window pops up. You'll see a friendly welcome message from the hotel's uniquely named AI assistant, like "Mar" or "Gustav," an input box to type your message, and a button to send it. The conversation appears just like a text message exchange on your phone.
                </p>
              </div>
            </div>
          </div>

          {/* How Does it Work */}
          <div className="bg-white/60 backdrop-blur-xl rounded-2xl border border-gray-200 p-8">
            <h3 className="text-2xl font-display font-bold text-gray-900 mb-6 text-center">
              How Does it Work?
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Globe className="w-6 h-6 text-blue-700" />
                </div>
                <div>
                  <h4 className="text-lg font-display font-bold text-gray-900 mb-2">
                    Hotel-Specific Knowledge
                  </h4>
                  <p className="text-gray-600 font-text leading-relaxed">
                    Each chatbot is specifically programmed with information from its own hotel. It learns from the hotel's website and internal documents to provide answers that are accurate and relevant to that specific location.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-green-700" />
                </div>
                <div>
                  <h4 className="text-lg font-display font-bold text-gray-900 mb-2">
                    Direct Hotel Contact When Needed
                  </h4>
                  <p className="text-gray-600 font-text leading-relaxed">
                    If Scout can't answer your question, it will provide you with the correct contact details for the specific hotel department that can help you. This ensures you get the most relevant and helpful assistance for your inquiry.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Scout Implementation Phases */}
        <div className="text-center mb-12 mt-20">
          <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-4">
            Scout Implementation Phases
          </h2>
        </div>

        {/* Phases Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {phases.map((phase) => {
            const IconComponent = phase.icon;
            return (
                             <Card 
                 key={phase.id}
                 className={`relative overflow-hidden bg-white/70 backdrop-blur-xl border ${phase.borderColor} shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:scale-105 group`}
               >
                 {/* Background gradient overlay */}
                 <div className={`absolute inset-0 bg-gradient-to-br ${phase.color} opacity-10 group-hover:opacity-20 transition-opacity duration-500`}></div>
                 
                 <CardContent className="p-6 relative z-10">
                   {/* Icon centered at top */}
                   <div className="flex justify-center mb-4">
                     <div className={`w-16 h-16 bg-gradient-to-br ${phase.color} rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                       <IconComponent className={`w-8 h-8 ${phase.textColor} group-hover:rotate-12 transition-transform duration-300`} />
                     </div>
                   </div>
                   
                   {/* Content */}
                   <div className="mb-4">
                     <div className="flex items-center justify-center space-x-2 mb-3">
                       <h3 className="text-xl font-display font-bold text-gray-900 group-hover:text-gray-800 transition-colors duration-300">
                         {phase.title}
                       </h3>
                       {phase.id === 6 && (
                         <span className="text-red-500 text-2xl animate-pulse"></span>
                       )}
                     </div>
                     
                     <p className="text-sm font-text font-semibold text-gray-700 leading-relaxed text-left">
                       {phase.description}
                     </p>
                   </div>
                   
                   {/* Details list */}
                   <ul className="space-y-2">
                     {phase.details.map((detail, index) => (
                       <li key={index} className="text-sm text-gray-600 font-text flex items-start group/item">
                         <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0 group-hover/item:bg-green-200 transition-colors duration-200">
                           <CheckCircle className="w-3 h-3 text-green-600" />
                         </div>
                         <span className="group-hover/item:text-gray-800 transition-colors duration-200">{detail}</span>
                       </li>
                     ))}
                   </ul>
                 </CardContent>
               </Card>
            );
          })}
        </div>

        {/* Footer Info */}
        <div className="mt-12 text-center">
          <div className="bg-white/40 backdrop-blur-xl rounded-2xl border border-gray-200 p-6 max-w-2xl mx-auto">
            <h3 className="text-xl font-display font-bold text-gray-900 mb-2">
              Ready to Get Started?
            </h3>
            <p className="text-gray-600 font-text">
              Each phase is carefully designed to ensure a smooth implementation process, from initial setup to full deployment. Our team works closely with you throughout every step.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
